import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { detectAmlTarget } from '@incpt/shared';
import { ScreenBackdrop } from './ScreenBackdrop';
import { ReauthFlow } from './ReauthFlow';
import { ChevronRight } from './icons';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useHaptic } from '../../hooks/useHaptic';
import { useMe } from '../../hooks/useMe';
import { useWallet } from '../../hooks/useWallet';
import { useTransfers } from '../../hooks/useTransfers';
import { useFeeQuote } from '../../hooks/useFeeQuote';
import { useAuthReady } from '../../hooks/useAuthReady';
import { SourceSelect, buildPaySources, pickRichestPaySource } from './SourceSelect';
import {
  amlErrorMessage,
  buyAmlCredits,
  createAmlCheck,
  fetchAmlCheck,
  fetchAmlChecks,
  fetchAmlConfig,
  fetchAmlMe,
  type AmlCheck,
} from '../../lib/aml';
import { categoryColor, parseAmlReport, riskColor, shortTarget } from '../../lib/aml-report';

type Phase = 'home' | 'buy' | 'result';

const PRESETS = [1, 5, 10, 25];

const usd = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ShieldMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3.2 19 6v6.2c0 4.2-2.9 7.2-7 8.6-4.1-1.4-7-4.4-7-8.6V6l7-2.8Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="m8.8 12.1 2.2 2.2 4.3-4.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Buy AML credits with USDT, then spend one credit per Reenergy screening. */
export function AmlScreen({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const { impact, notify } = useHaptic();
  const { canFetch, initData } = useAuthReady();
  const { me } = useMe();
  const { wallet, balances } = useWallet();
  const { send } = useTransfers();
  const qc = useQueryClient();
  const tgBack = hasTelegramBackButton();

  const [phase, setPhase] = useState<Phase>('home');
  const [target, setTarget] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [active, setActive] = useState<AmlCheck | null>(null);
  const submittingRef = useRef(false);
  const checkFlightRef = useRef(false);
  const checkAttemptRef = useRef<{ target: string; key: string } | null>(null);
  const payIdemRef = useRef<string | null>(null);
  const paidTransferIdRef = useRef<string | null>(null);
  const [paidTransferId, setPaidTransferId] = useState<string | null>(null);

  const back = () => {
    if (busy) return;
    if (phase !== 'home') {
      setError(null);
      setPhase('home');
      return;
    }
    onBack();
  };
  useBackButton(back);

  const configQ = useQuery({
    queryKey: ['aml-config'],
    enabled: canFetch,
    queryFn: () => fetchAmlConfig(initData),
  });
  const meQ = useQuery({
    queryKey: ['aml-me'],
    enabled: canFetch,
    queryFn: () => fetchAmlMe(initData),
  });
  const checksQ = useQuery({
    queryKey: ['aml-checks'],
    enabled: canFetch,
    queryFn: () => fetchAmlChecks(initData),
  });

  const config = configQ.data;
  const credits = meQ.data?.credits ?? 0;
  const unitPrice = config?.unitPriceUsd ?? 0.5;
  const maxQuantity = config?.maxQuantity ?? 100;
  const enabled = config?.enabled ?? false;
  const treasury = config?.treasury ?? null;
  const preview = detectAmlTarget(target);
  const recent = checksQ.data?.checks ?? [];

  const sources = buildPaySources(wallet, balances, t('nav.wallet')).filter(
    (s) => s.chain === 'trx' && s.token === 'USDT',
  );
  const source = sources.find((s) => s.key === sourceKey) ?? pickRichestPaySource(sources);
  const service = Math.round(quantity * unitPrice * 100) / 100;
  const { feeUsd: networkFee, maxFeeUsd, fresh: feeFresh } = useFeeQuote(
    'trx',
    'USDT',
    treasury ?? undefined,
    source?.address,
  );
  const reserveFee = maxFeeUsd ?? networkFee ?? 0;
  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const canPay =
    enabled &&
    !!source &&
    !!treasury &&
    !!wallet?.id &&
    feeFresh &&
    (paidTransferId ? true : source.balance + 1e-9 >= service + reserveFee);

  useEffect(() => {
    if (!active) return;
    if (active.status !== 'pending' && active.status !== 'processing') return;
    let stop = false;
    let timer = 0;
    const tick = async () => {
      try {
        const { check } = await fetchAmlCheck(initData, active.id);
        if (stop) return;
        setActive(check);
        if (check.status === 'pending' || check.status === 'processing') {
          timer = window.setTimeout(() => void tick(), 2000);
          return;
        }
        void qc.invalidateQueries({ queryKey: ['aml-me'] });
        void qc.invalidateQueries({ queryKey: ['aml-checks'] });
      } catch {
        if (!stop) timer = window.setTimeout(() => void tick(), 3000);
      }
    };
    timer = window.setTimeout(() => void tick(), 1200);
    return () => {
      stop = true;
      window.clearTimeout(timer);
    };
  }, [active?.id, active?.status, initData, qc]);

  const explain = (err: unknown) => {
    const code = amlErrorMessage(err, '');
    if (code === 'NO_CREDITS') return t('aml.noCredits');
    if (code === 'DISABLED') return t('aml.disabled');
    if (code === 'NOT_FOUND') return t('aml.notFound');
    if (code === 'TARGET_INVALID') return t('aml.invalid');
    if (code === 'INSUFFICIENT') return t('aml.insufficient');
    return t('aml.errGeneric');
  };

  const runCheck = async () => {
    if (!preview || credits < 1 || checkFlightRef.current) return;
    const trimmed = target.trim();
    if (!checkAttemptRef.current || checkAttemptRef.current.target !== trimmed) {
      checkAttemptRef.current = { target: trimmed, key: crypto.randomUUID() };
    }
    checkFlightRef.current = true;
    impact('medium');
    setBusy(true);
    setError(null);
    try {
      const { check } = await createAmlCheck(initData, trimmed, checkAttemptRef.current.key);
      if (check.status === 'completed' || check.status === 'failed' || check.status === 'refunded') {
        checkAttemptRef.current = null;
      }
      setActive(check);
      setPhase('result');
      setTarget('');
      void qc.invalidateQueries({ queryKey: ['aml-me'] });
      void qc.invalidateQueries({ queryKey: ['aml-checks'] });
    } catch (e) {
      notify('error');
      setError(explain(e));
      void qc.invalidateQueries({ queryKey: ['aml-me'] });
      void qc.invalidateQueries({ queryKey: ['aml-checks'] });
    } finally {
      checkFlightRef.current = false;
      setBusy(false);
    }
  };

  const settle = async (transferId: string) => {
    const res = await buyAmlCredits(initData, transferId);
    notify('success');
    setPaidTransferId(null);
    paidTransferIdRef.current = null;
    payIdemRef.current = null;
    setPhase('home');
    setError(null);
    void qc.invalidateQueries({ queryKey: ['aml-me'] });
    void qc.invalidateQueries({ queryKey: ['balances'] });
    return res.purchase.quantity;
  };

  const runBuy = async (token?: string) => {
    if (!wallet?.id || !source || !treasury) return;
    if (paidTransferIdRef.current) {
      setBusy(true);
      try {
        await settle(paidTransferIdRef.current);
      } catch (e) {
        notify('error');
        setError(explain(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      impact('medium');
      if (!payIdemRef.current) payIdemRef.current = crypto.randomUUID();
      const transfer = await send.mutateAsync({
        walletId: wallet.id,
        chain: 'trx',
        token: 'USDT',
        to: treasury,
        amount: String(service),
        reauthToken: token,
        fromAddressId: source.addressId,
        fromIndex: source.fromIndex,
        idempotencyKey: payIdemRef.current,
        awaitSettlement: true,
      });
      paidTransferIdRef.current = transfer.id;
      setPaidTransferId(transfer.id);
      await settle(transfer.id);
    } catch (e) {
      notify('error');
      const msg = String(e);
      if (msg.includes('reauth_required')) setReauthOpen(true);
      else setError(explain(e));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const openCheck = (check: AmlCheck) => {
    setActive(check);
    setPhase('result');
    setError(null);
  };

  const riskLabel = (level: number | null) => {
    if (level == null) return '—';
    if (level >= 0.5) return t('aml.high');
    if (level >= 0.25) return t('aml.medium');
    return t('aml.low');
  };

  return (
    <div className="relative min-h-dvh bg-holo">
      <ScreenBackdrop full />
      <div className="relative z-10 flex min-h-dvh flex-col px-4 pb-[max(2.5rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              type="button"
              onClick={back}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label="Back"
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="flex-1 text-[20px] font-medium text-ink">{t('aml.title')}</h1>
          <span className="rounded-full bg-[#F7931A]/15 px-3 py-1 text-[13px] font-semibold text-[#C56A00]">
            {t('aml.credits', { count: credits })}
          </span>
        </header>

        {phase === 'home' && (
          <div className="mt-3 flex flex-col gap-3">
            {!target.trim() && (
              <div className="flex flex-col items-center px-4 pb-2 pt-4 text-center">
                <div className="grid h-[76px] w-[76px] place-items-center rounded-[24px] bg-gradient-to-br from-[#F7931A] to-[#FFB45B] text-white shadow-[0_10px_24px_rgba(247,147,26,0.35)]">
                  <ShieldMark size={36} />
                </div>
                <h2 className="mt-4 text-[22px] font-semibold tracking-[-0.4px] text-ink">
                  {t('aml.homeTitle')}
                </h2>
                <p className="mt-1.5 max-w-[280px] text-[14px] leading-[1.5] text-muted">
                  {t('aml.homeSubtitle')}
                </p>
              </div>
            )}

            <div className="relative overflow-visible p-4">
              {/* Card is a sibling. iOS clips <textarea> text to an ancestor
                  border-radius and cuts the placeholder off at the corner. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-[22px] bg-white shadow-card"
              />
              <textarea
                value={target}
                onChange={(e) => {
                  setTarget(e.target.value);
                  setError(null);
                }}
                placeholder={t('aml.placeholder')}
                rows={3}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className="relative w-full resize-none appearance-none rounded-none border-0 bg-transparent text-[15px] leading-[1.45] text-ink outline-none placeholder:text-black/25"
              />
              <div className="relative mt-2 flex items-center justify-between">
                {target ? (
                  <button
                    type="button"
                    onClick={() => setTarget('')}
                    className="text-[13px] font-semibold text-[#C56A00]"
                  >
                    {t('aml.clear')}
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard
                      .readText()
                      .then((text) => {
                        if (text.trim()) setTarget(text.trim());
                      })
                      .catch(() => {});
                  }}
                  className="text-[13px] font-semibold text-[#C56A00]"
                >
                  {t('aml.paste')}
                </button>
              </div>
            </div>

            {target.trim() && (
              <div
                className={`rounded-[18px] bg-white px-4 py-3 shadow-card ${
                  preview ? '' : 'ring-1 ring-[#F05252]/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`grid h-8 w-8 place-items-center rounded-full ${
                      preview ? 'bg-[#22B573]/15 text-[#22B573]' : 'bg-[#F05252]/12 text-[#F05252]'
                    }`}
                  >
                    {preview ? '✓' : '!'}
                  </span>
                  <span className="flex-1 text-[14px] text-ink">
                    {preview ? t('aml.detected') : t('aml.invalid')}
                  </span>
                  {preview && (
                    <span className="text-[14px] font-semibold text-ink">
                      {preview.type === 'transaction' ? t('aml.transaction') : t('aml.address')}
                    </span>
                  )}
                </div>
                {preview && (
                  <div className="mt-3 flex items-center gap-3 border-t border-black/[0.06] pt-3">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-[#22B573]/15 text-[#22B573]">
                      <ShieldMark size={16} />
                    </span>
                    <span className="flex-1 text-[14px] text-muted">{t('aml.network')}</span>
                    <span className="text-[14px] font-semibold text-ink">
                      {preview.network ?? t('aml.networkAuto')}
                    </span>
                  </div>
                )}
              </div>
            )}

            {error && phase === 'home' && (
              <p className="text-center text-[13px] font-semibold text-negative">{error}</p>
            )}
            {!enabled && config && (
              <p className="text-center text-[13px] font-medium text-muted">{t('aml.disabled')}</p>
            )}

            {target.trim() && preview && (
              <button
                type="button"
                disabled={busy || !enabled}
                onClick={() => {
                  if (credits > 0) void runCheck();
                  else {
                    setError(null);
                    setPhase('buy');
                  }
                }}
                className="h-12 rounded-[15px] bg-ink text-[15px] font-semibold text-white active:opacity-90 disabled:opacity-40"
              >
                {busy ? t('aml.checking') : credits > 0 ? t('aml.run') : t('aml.buy')}
              </button>
            )}

            {!target.trim() && (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setPhase('buy');
                }}
                className="flex items-center gap-3 rounded-[20px] bg-white p-4 text-left shadow-card active:opacity-80"
              >
                <span className="grid h-10 w-10 place-items-center rounded-[14px] bg-[#F7931A]/12 text-[#F7931A]">
                  <ShieldMark size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-medium text-ink">{t('aml.buy')}</span>
                  <span className="block text-[13px] text-muted">
                    {t('aml.creditsHint', { price: usd(unitPrice) })}
                  </span>
                </span>
                <ChevronRight size={16} className="text-black/25" />
              </button>
            )}

            {!target.trim() && recent.length > 0 && (
              <div className="mt-2">
                <h3 className="mb-2 px-1 text-[13px] font-medium uppercase tracking-[0.4px] text-muted">
                  {t('aml.recent')}
                </h3>
                <div className="overflow-hidden rounded-[20px] bg-white shadow-card">
                  {recent.map((check, index) => {
                    const pending = check.status === 'pending' || check.status === 'processing';
                    const color = pending ? '#85858D' : riskColor(check.riskLevel);
                    return (
                      <button
                        key={check.id}
                        type="button"
                        onClick={() => openCheck(check)}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left active:bg-black/[0.03] ${
                          index > 0 ? 'border-t border-black/[0.06]' : ''
                        }`}
                      >
                        <span
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                          style={{ backgroundColor: `${color}22`, color }}
                        >
                          <ShieldMark size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14px] font-medium text-ink">
                            {shortTarget(check.target)}
                          </span>
                          <span className="block text-[12px] text-muted">
                            {check.network === 'AUTO' ? t('aml.networkAuto') : check.network}
                            {' · '}
                            {check.type === 'transaction' ? t('aml.transaction') : t('aml.address')}
                          </span>
                        </span>
                        <span className="text-[14px] font-semibold" style={{ color }}>
                          {pending
                            ? '…'
                            : check.riskLevel == null
                              ? '—'
                              : `${Math.round(check.riskLevel * 100)}%`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {phase === 'buy' && (
          <div className="mt-3 flex flex-col gap-3">
            <div className="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-[#1B1B20] to-[#3A2718] p-5 text-white">
              <div className="pointer-events-none absolute -right-6 -top-8 h-28 w-28 rounded-full bg-[#F7931A]/30 blur-2xl" />
              <div className="relative flex items-center justify-between">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-[#FFB45B]">
                  <ShieldMark size={18} />
                </span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-[12px] font-semibold tracking-[0.3px]">
                  USDT · TRC20
                </span>
              </div>
              <p className="relative mt-5 text-[13px] text-white/60">{t('aml.unitPrice')}</p>
              <p className="relative text-[34px] font-semibold tracking-[-0.8px]">
                ${usd(unitPrice)}
                <span className="text-[16px] font-medium text-white/55"> / {t('aml.checksShort')}</span>
              </p>
              <p className="relative mt-1 text-[13px] text-white/55">{t('aml.buyHint')}</p>
            </div>

            <div className="flex items-center rounded-[22px] bg-white px-3 py-4 shadow-card">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="grid h-11 w-11 place-items-center rounded-full bg-black/[0.05] text-[22px] text-ink"
                aria-label="-"
              >
                −
              </button>
              <div className="flex-1 text-center">
                <div className="text-[12px] uppercase tracking-[0.4px] text-muted">{t('aml.quantity')}</div>
                <div className="text-[36px] font-semibold leading-none tracking-[-1px] text-ink">{quantity}</div>
                <div className="mt-1 text-[13px] text-muted">{t('aml.checksShort')}</div>
              </div>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                className="grid h-11 w-11 place-items-center rounded-full bg-black/[0.05] text-[22px] text-ink"
                aria-label="+"
              >
                +
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {PRESETS.filter((n) => n <= maxQuantity).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setQuantity(preset)}
                  className={`h-11 rounded-[14px] text-[15px] font-semibold ${
                    quantity === preset ? 'bg-ink text-white' : 'bg-white text-ink shadow-card'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <p className="px-1 text-center text-[12px] text-muted">{t('aml.max', { max: maxQuantity })}</p>

            <div className="rounded-[20px] bg-white p-4 shadow-card">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[15px] font-medium text-ink">{t('aml.summary')}</span>
                <span className="rounded-full bg-[#F7931A]/12 px-2.5 py-1 text-[13px] font-semibold text-[#C56A00]">
                  ${usd(service + (networkFee ?? 0))}
                </span>
              </div>
              <Row label={`${quantity} × $${usd(unitPrice)}`} value={`$${usd(service)}`} />
              <Row label={t('aml.networkFee')} value={networkFee == null ? '…' : `$${usd(networkFee)}`} />
              <div className="my-2 border-t border-black/[0.06]" />
              <Row
                label={t('aml.total')}
                value={`$${usd(service + (networkFee ?? 0))}`}
                strong
              />
            </div>

            {sources.length > 1 && (
              <SourceSelect
                sources={sources}
                value={source?.key ?? null}
                onChange={setSourceKey}
                required={service + reserveFee}
              />
            )}

            <div className="flex items-center justify-between px-1 text-[13px]">
              <span className="text-muted">{t('aml.balance')}</span>
              <span className="font-semibold text-ink">{usd(source?.balance ?? 0)} USDT</span>
            </div>

            {error && <p className="text-center text-[13px] font-semibold text-negative">{error}</p>}
            {!enabled && <p className="text-center text-[13px] font-medium text-muted">{t('aml.disabled')}</p>}
            {enabled &&
              !paidTransferId &&
              (!source || (feeFresh && source.balance + 1e-9 < service + reserveFee)) && (
                <p className="text-center text-[13px] font-semibold text-negative">{t('aml.insufficient')}</p>
              )}

            <button
              type="button"
              disabled={busy || (!canPay && !paidTransferId)}
              onClick={() => {
                if (needsReauth && !paidTransferId) {
                  setReauthOpen(true);
                  return;
                }
                void runBuy();
              }}
              className="h-12 rounded-[15px] bg-ink text-[15px] font-semibold text-white active:opacity-90 disabled:opacity-40"
            >
              {busy ? t('aml.paying') : paidTransferId ? t('aml.retry') : t('aml.pay', { amount: usd(service) })}
            </button>
          </div>
        )}

        {phase === 'result' && active && (
          <Result
            check={active}
            language={i18n.language}
            riskLabel={riskLabel(active.riskLevel)}
            onAgain={() => {
              setActive(null);
              setPhase('home');
            }}
          />
        )}
      </div>

      {reauthOpen && (
        <ReauthFlow
          initData={initData}
          biometricEnabled={!!me?.biometricEnabled}
          reason={t('aml.reauth')}
          onCancel={() => setReauthOpen(false)}
          onSuccess={(token) => {
            setReauthOpen(false);
            void runBuy(token);
          }}
        />
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={strong ? 'text-[15px] font-semibold text-ink' : 'text-[14px] text-muted'}>{label}</span>
      <span className={strong ? 'text-[15px] font-semibold text-ink' : 'text-[14px] font-medium text-ink'}>
        {value}
      </span>
    </div>
  );
}

function Result({
  check,
  language,
  riskLabel,
  onAgain,
}: {
  check: AmlCheck;
  language: string;
  riskLabel: string;
  onAgain: () => void;
}) {
  const { t } = useTranslation();
  const pending = check.status === 'pending' || check.status === 'processing';
  const failed = check.status === 'failed' || check.status === 'refunded';
  const report = parseAmlReport(check.exposure);
  const color = riskColor(check.riskLevel);
  const pct = check.riskLevel == null ? 0 : Math.round(check.riskLevel * 100);

  if (pending) {
    return (
      <div className="mt-16 flex flex-col items-center text-center">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-[#F7931A]/15 text-[#F7931A]">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#F7931A] border-t-transparent" />
        </div>
        <h2 className="mt-4 text-[20px] font-semibold text-ink">{t('aml.checking')}</h2>
        <p className="mt-1.5 max-w-[260px] text-[14px] text-muted">{t('aml.checkingHint')}</p>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex flex-col items-center rounded-[24px] bg-white px-5 py-6 text-center shadow-card">
        <div
          className="grid h-28 w-28 place-items-center rounded-full"
          style={{ background: `conic-gradient(${color} ${pct * 3.6}deg, rgba(0,0,0,0.06) 0)` }}
        >
          <div className="grid h-[92px] w-[92px] place-items-center rounded-full bg-white">
            <span className="text-[28px] font-semibold tracking-[-0.6px]" style={{ color }}>
              {check.riskLevel == null ? '—' : `${pct}%`}
            </span>
          </div>
        </div>
        <div className="mt-3 text-[16px] font-semibold" style={{ color }}>
          {failed ? t('aml.failed') : riskLabel}
        </div>
        {report.entityName && <div className="mt-1 text-[15px] font-medium text-ink">{report.entityName}</div>}
        {report.entityCategory && <div className="text-[13px] text-muted">{report.entityCategory}</div>}
        <div className="mt-2 text-[12px] text-muted">
          {check.network === 'AUTO' ? t('aml.networkAuto') : check.network}
          {' · '}
          {check.type === 'transaction' ? t('aml.transaction') : t('aml.address')}
        </div>
        <div className="mt-1 max-w-full truncate font-mono text-[12px] text-ink/70">{shortTarget(check.target)}</div>
      </div>

      {report.bars.length > 0 && (
        <div className="rounded-[20px] bg-white p-4 shadow-card">
          <h3 className="mb-3 text-[15px] font-medium text-ink">{t('aml.exposure')}</h3>
          <div className="flex flex-col gap-3">
            {report.bars.map((bar) => (
              <div key={`${bar.category}-${bar.label}`}>
                <div className="mb-1 flex items-center justify-between text-[13px]">
                  <span className="text-ink">{labelFor(language, bar.category, bar.label)}</span>
                  <span className="font-semibold text-ink">{bar.share.toFixed(bar.share >= 10 ? 0 : 1)}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-black/[0.06]">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(100, bar.share)}%`, background: categoryColor(bar.category) }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.risks.length > 0 && (
        <div className="rounded-[20px] bg-white p-4 shadow-card">
          <h3 className="mb-2 text-[15px] font-medium text-ink">{t('aml.risks')}</h3>
          {report.risks.map((risk) => (
            <div key={`${risk.category}-${risk.level}`} className="flex items-center gap-2 py-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: categoryColor(risk.category) }}
              />
              <span className="flex-1 text-[14px] text-ink">{labelFor(language, risk.category, risk.label)}</span>
              <span className="text-[12px] font-semibold uppercase text-muted">{risk.level}</span>
            </div>
          ))}
        </div>
      )}

      {check.reportUrl && (
        <a
          href={check.reportUrl}
          target="_blank"
          rel="noreferrer"
          className="flex h-12 items-center justify-center rounded-[15px] bg-white text-[15px] font-semibold text-ink shadow-card"
        >
          {t('aml.report')}
        </a>
      )}

      <button
        type="button"
        onClick={onAgain}
        className="h-12 rounded-[15px] bg-ink text-[15px] font-semibold text-white active:opacity-90"
      >
        {t('aml.newCheck')}
      </button>
    </div>
  );
}

const RU: Record<string, string> = {
  exchange: 'Биржа',
  high_risk_exchange: 'Высокорисковая биржа',
  mixer: 'Миксер',
  gambling: 'Азартные игры',
  sanctions: 'Санкции',
  sanctioned: 'Санкционный субъект',
  scam: 'Мошенничество',
  darknet_market: 'Даркнет',
  defi: 'DeFi',
  dex: 'DEX',
  bridge: 'Мост',
  mining: 'Майнинг',
  stolen_funds: 'Украденные средства',
  ransomware: 'Вымогательство',
};

function labelFor(language: string, category: string, fallback: string) {
  if (!language.toLowerCase().startsWith('ru')) return fallback;
  return RU[category.toLowerCase()] ?? fallback;
}
